import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useState } from 'react'
import type { ChangeEvent, ReactElement } from 'react'
import {
  type CsvPreviewGroup,
  type CsvRow,
  buildCsvPreview,
  parseCsvRows,
} from '../utils/csvImport'
import { DEFAULT_HABIT_CATEGORY } from '../utils/habitCategories'
import { loadNextHabitSortOrder } from '../utils/habitData'
import { ModalBackdrop } from './ModalBackdrop'

interface CsvImportSheetProps {
  userId: string
  onClose: () => void
  onImportComplete: () => void
}

const EMPTY_IMPORT_ERROR =
  'No valid rows found. Make sure the CSV has Habit, Date, Value columns with at least one row where Value=1.'

function normalizeHabitName(name: string): string {
  return name.toLowerCase()
}

export function CsvImportSheet({
  userId,
  onClose,
  onImportComplete,
}: CsvImportSheetProps): ReactElement {
  const [rows, setRows] = useState<CsvRow[]>([])
  const [preview, setPreview] = useState<CsvPreviewGroup[]>([])
  const [importing, setImporting] = useState(false)
  const [done, setDone] = useState(false)
  const [importSummary, setImportSummary] = useState('')
  const [error, setError] = useState('')

  function handleFileChange(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0]
    if (!file) return

    setError('')
    const reader = new FileReader()

    reader.onload = (readerEvent) => {
      const parsedRows = parseCsvRows(String(readerEvent.target?.result ?? ''))

      if (parsedRows.length === 0) {
        setError(EMPTY_IMPORT_ERROR)
        setRows([])
        setPreview([])
        return
      }

      setRows(parsedRows)
      setPreview(buildCsvPreview(parsedRows))
    }

    reader.readAsText(file)
  }

  async function handleImport(): Promise<void> {
    if (rows.length === 0) return

    setImporting(true)
    setError('')

    const habitNames = [...new Set(rows.map((row) => row.habitName))]
    const { data: existingHabits } = await supabase
      .schema('habit')
      .from('habits')
      .select('id, name')
      .eq('user_id', userId)
      .in('name', habitNames)

    const habitMap = new Map<string, string>()
    for (const habit of existingHabits ?? []) {
      habitMap.set(normalizeHabitName(habit.name), habit.id)
    }

    const missingHabitNames = habitNames.filter(
      (name) => !habitMap.has(normalizeHabitName(name)),
    )

    if (missingHabitNames.length > 0) {
      const nextSortOrder = await loadNextHabitSortOrder(userId)
      const { data: createdHabits } = await supabase
        .schema('habit')
        .from('habits')
        .insert(
          missingHabitNames.map((name, index) => ({
            name,
            user_id: userId,
            frequency_type: 'daily' as const,
            description: null,
            category: DEFAULT_HABIT_CATEGORY,
            pinned: false,
            sort_order: nextSortOrder + index,
          })),
        )
        .select('id, name')

      for (const habit of createdHabits ?? []) {
        habitMap.set(normalizeHabitName(habit.name), habit.id)
      }
    }

    const logs = rows
      .map((row) => ({
        habit_id: habitMap.get(normalizeHabitName(row.habitName)),
        user_id: userId,
        period_key: row.date,
      }))
      .filter((log) => log.habit_id != null) as {
      habit_id: string
      user_id: string
      period_key: string
    }[]

    const { error: insertError } = await supabase
      .schema('habit')
      .from('habit_logs')
      .upsert(logs, {
        onConflict: 'habit_id,period_key',
        ignoreDuplicates: true,
      })

    setImporting(false)

    if (insertError) {
      setError(`Import failed: ${insertError.message}`)
      return
    }

    setImportSummary(
      `Imported ${logs.length} log${logs.length === 1 ? '' : 's'} for ${
        habitNames.length
      } habit${habitNames.length === 1 ? '' : 's'}.`,
    )
    setDone(true)
    onImportComplete()
  }

  return (
    <>
      <ModalBackdrop ariaLabel="Close import" onClose={onClose} />
      <div className="fixed inset-0 z-50 m-auto h-fit max-h-[85vh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-2xl bg-white px-4 pt-6 pb-8 shadow-2xl sm:shadow-md">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 text-lg">
            Import from CSV
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="flex flex-col items-center gap-4 py-6">
            <span className="text-4xl">✅</span>
            <p className="text-center font-medium text-gray-800">
              {importSummary}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-indigo-600 px-6 py-2.5 font-semibold text-sm text-white"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <p className="mb-4 text-gray-500 text-sm">
              Upload a CSV with{' '}
              <code className="rounded bg-gray-100 px-1">Habit</code>,{' '}
              <code className="rounded bg-gray-100 px-1">Date</code>, and{' '}
              <code className="rounded bg-gray-100 px-1">Value</code> columns.
              Rows where Value=1 are imported as completions.
            </p>
            <label className="mb-4 flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-gray-300 border-dashed py-5 font-medium text-gray-500 text-sm transition-colors hover:border-indigo-400 hover:text-indigo-600">
              <CloudArrowUpIcon className="h-5 w-5" />
              <span>Choose CSV file</span>
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
            {error && (
              <p className="mb-3 rounded-xl bg-red-50 px-3 py-2 text-red-600 text-sm">
                {error}
              </p>
            )}
            {preview.length > 0 && (
              <>
                <p className="mb-2 font-medium text-gray-500 text-xs uppercase tracking-wide">
                  Preview — {rows.length} completion
                  {rows.length === 1 ? '' : 's'} across {preview.length} habit
                  {preview.length === 1 ? '' : 's'}
                </p>
                <div className="mb-4 flex flex-col gap-2">
                  {preview.map((group) => (
                    <div
                      key={group.habitName}
                      className="flex items-center justify-between rounded-xl border bg-gray-50 px-3 py-2"
                    >
                      <div>
                        <p className="font-medium text-gray-900 text-sm">
                          {group.habitName}
                        </p>
                        <p className="text-gray-400 text-xs">
                          {group.firstDate === group.lastDate
                            ? group.firstDate
                            : `${group.firstDate} → ${group.lastDate}`}
                        </p>
                      </div>
                      <span className="rounded-full bg-indigo-100 px-2 py-0.5 font-semibold text-indigo-700 text-xs">
                        {group.count}×
                      </span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={importing}
                  className="w-full rounded-xl bg-indigo-600 px-4 py-3 font-semibold text-base text-white active:bg-indigo-700 disabled:opacity-50"
                >
                  {importing ? 'Importing…' : 'Confirm Import'}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </>
  )
}
